import React, { useState } from "react";
import landingBg from "../img/destination.jpg";
import Model from "@tripian/model";
import useTranslate from "../../../hooks/useTranslate";
import { useSearchParams, useNavigate } from "react-router-dom";
import moment from "moment";
import CitySearchInput, { CityOption } from "./CitySearchInput";
import ValaiDateRangePicker from "./ValaiDateRangePicker";
import "./style.scss";

const YourPersonalTripPlanner = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cityId = searchParams.get("cityId");
  const arrivalDate = searchParams.get("arrivalDate");
  const departureDate = searchParams.get("departureDate");

  const [tripProfile, setTripProfile] = useState<Model.TripProfile>({
    cityId: cityId ? parseInt(cityId) : 0,
    arrivalDatetime:
      arrivalDate ||
      moment()
        .add(1, "days")
        .set({ hour: 9, minute: 0 })
        .format("YYYY-MM-DDTHH:mm:ss"),
    departureDatetime:
      departureDate ||
      moment()
        .add(4, "days")
        .set({ hour: 18, minute: 0 })
        .format("YYYY-MM-DDTHH:mm:ss"),
    answers: [],
    numberOfAdults: 1,
    companionIds: [],
    doNotGenerate: 0,
    excludeHash: [],
  });

  const handleCitySelect = (city: CityOption) => {
    setTripProfile(prev => ({ ...prev, cityId: city.id }));
  };

  const handleCityClear = () => {
    setTripProfile(prev => ({ ...prev, cityId: 0 }));
  };

  const handleDateChange = (startDate: moment.Moment, endDate: moment.Moment) => {
    setTripProfile(prev => ({
      ...prev,
      arrivalDatetime: startDate.format('YYYY-MM-DDTHH:mm:ss'),
      departureDatetime: endDate.format('YYYY-MM-DDTHH:mm:ss'),
    }));
  };

  const handleStartPlanning = () => {
    if (tripProfile.cityId === 0) {
      return;
    }
    navigate(`/trip-planner?cityId=${tripProfile.cityId}&arrivalDate=${tripProfile.arrivalDatetime}&departureDate=${tripProfile.departureDatetime}`);
  };

  return (
    <div className="relative w-full h-screen">
      {/* Background Image */}
      <img
        src={landingBg}
        alt="Valai Chicago"
        className="absolute inset-0 w-full h-full object-cover landingBg"
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-20" />

      {/* Content Container */}
      <div className="relative z-10 flex flex-col justify-center items-center h-full px-4 md:px-8 text-center">
        {/* Main Title */}
        <h1 className="text-white font-55-h font-semibold mb-4 syne-fm">
          {t("landing.yourPersonalTripPlanner")}
        </h1>

        {/* Description */}
        <p className="text-white font-24-h text-lg md:text-xl max-w-4xl mb-12 leading-relaxed inter-fm">
            Explore over 500 cities worldwide with personalized recommendations and daily itineraries and book unforgettable experiences — all in one place.
        </p>

        {/* Search Form */}
        <div className="bg-white border-radius-56-c pd-start-trip-bg w-full max-w-5xl shadow-2xl">
          <div className="flex flex-col md:flex-row gap-2">
            {/* Where to go input */}
            <CitySearchInput
              selectedCityId={cityId ? parseInt(cityId) : undefined}
              onCitySelect={handleCitySelect}
              onClear={handleCityClear}
              className="flex-1"
              showValaiStyle={true}
            />

            {/* Date inputs */}
            <ValaiDateRangePicker
              startDate={moment(tripProfile.arrivalDatetime)}
              endDate={moment(tripProfile.departureDatetime)}
              onDateChange={handleDateChange}
              className="flex-1"
            />

          </div>
        </div>
          {/* Search button */}
          <button
              onClick={handleStartPlanning}
              disabled={tripProfile.cityId === 0}
              className={`mt-3 start-planing-btn border-radius-56-c font-semibold px-8 py-3 transition-colors whitespace-nowrap flex items-center gap-2 ${
                tripProfile.cityId === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
              }`}
          >
              <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-5 h-5 rotate-300"
              >
                  <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                  />
              </svg>

              {t('valai.startPlanning')}

          </button>
      </div>
    </div>
  );
};

export default YourPersonalTripPlanner;